// newProjectDraftStore — localStorage-backed draft for the redesigned
// 7-step New Project wizard (frontend/src/pages/new-project).
//
// Kept entirely separate from intakeDraftStore (the live 4-step
// SmartIntakePage) so the two flows never clobber each other's saved
// work: different localStorage key, its own step range (1..7), and the
// two UI-only steps (Security, Team) that have no backend representation.
//
// As with the original draft store we persist *form fields only* —
// pending file uploads (File objects) can't be JSON-serialised, so they
// are persisted separately in IndexedDB via lib/pendingFileStore.ts
// (slot 'new_project') and restored on resume.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { AudienceTag, ShapeId } from '@/components/intake/shapes'
import type { InterventionBudget, RegulatoryFlag } from '@/types'

export interface NewProjectDraft {
  name: string
  problem_statement: string
  shape: ShapeId | null
  vertical: string | null
  regulatory: RegulatoryFlag[]
  regulatoryUserEdited: boolean
  audience: AudienceTag[]
  cadence: InterventionBudget
  document_owner: string
  document_approver: string
  ai_governance_disposition: 'in_scope' | 'not_applicable' | null
  ai_governance_ratified_by: string
  ai_governance_rationale: string
  cloud_provider: string
  primary_region: string
  container_runtime: string
  orchestrator: string
  iac_framework: string
  secrets_store: string
  secrets_rotation_policy: string
  oncall_primary_rotation: string
  oncall_runbook_index: string
  oncall_pager_severity_thresholds: string
  // UI-only — Security (step 04) and Team (step 06) have no backend
  // field today, so these never leave the browser. Persisted here so a
  // reload mid-wizard doesn't lose them.
  securityControls: string[]
  infraSelections: string[]
  teamAssignments: Record<string, string[]>
  projectMembers: string[]
  /** Wizard position 1..7 the user was on when they bounced. */
  step: number
  /** ISO timestamp so the resume banner can say "saved 8 min ago". */
  savedAt: string | null
}

interface NewProjectDraftStore {
  draft: NewProjectDraft | null
  saveDraft: (draft: Omit<NewProjectDraft, 'savedAt'>) => void
  clearDraft: () => void
  hasDraft: () => boolean
}

export const useNewProjectDraftStore = create<NewProjectDraftStore>()(
  persist(
    (set, get) => ({
      draft: null,
      saveDraft: (draft) =>
        set({ draft: { ...draft, savedAt: new Date().toISOString() } }),
      clearDraft: () => set({ draft: null }),
      hasDraft: () => {
        const d = get().draft
        if (!d) return false
        return d.name.trim().length > 0 || d.problem_statement.trim().length > 0
      },
    }),
    { name: 'ai_alm_new_project_draft_v1' },
  ),
)
